/* eslint-disable styled-components-a11y/anchor-is-valid, styled-components-a11y/click-events-have-key-events, styled-components-a11y/no-static-element-interactions */
import type { LinkProps } from 'next/link';
import Link from 'next/link';
import { useRouter } from 'next/router';
import type { RefObject } from 'react';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import type { Language } from '@peloton/internationalize/models/language';

export type NavLinkProps = LinkProps & {
  activeClassName?: string;
  alias?: string;
  className?: string;
  children?: React.ReactNode;
  exact?: boolean;
  id?: string;
  innerRef?: RefObject<HTMLAnchorElement>;
  isActive?: (() => boolean) | boolean;
  isExternal?: boolean;
  lang?: Language;
  tabIndex?: number;
  target?: string;
  title?: string;
};

const NavLink = ({
  activeClassName,
  alias,
  className,
  children,
  exact,
  href,
  id,
  innerRef,
  isActive,
  isExternal,
  lang,
  onClick,
  tabIndex,
  title,
  ...props
}: NavLinkProps) => {
  let isActiveClassName = false;
  const { asPath, isReady } = useRouter();
  const [computedClassName, setComputedClassName] = useState(className);

  useEffect(() => {
    // Check if the router fields are updated client-side
    if (isReady) {
      if (typeof isActive === 'boolean') {
        isActiveClassName = isActive;
      } else if (exact) {
        isActiveClassName =
          asPath === href || (typeof href === 'object' && asPath === href.pathname);
      } else {
        isActiveClassName =
          asPath.includes(String(href)) ||
          (typeof href === 'object' && asPath.includes(href.pathname!));
      }

      isActiveClassName
        ? setComputedClassName(`${className} ${activeClassName}`)
        : setComputedClassName(className);
    }
  }, [activeClassName, asPath, className, isReady]);

  return isExternal ? (
    <a
      target="_blank"
      {...props}
      href={href as string}
      className={computedClassName}
      rel="noopener noreferrer"
    >
      {children}
    </a>
  ) : (
    <Link
      as={alias}
      href={href}
      id={id}
      scroll={false}
      passHref
      legacyBehavior
      {...props}
    >
      <StyledLink
        className={computedClassName}
        data-test-id={props['data-test-id']}
        ref={innerRef}
        lang={lang}
        onClick={onClick}
        tabIndex={tabIndex}
        title={title}
      >
        {children}
      </StyledLink>
    </Link>
  );
};

const StyledLink = styled.a``;

export default NavLink;
